import { FormikProps } from 'formik';
import { useAuthStore, useCheckoutStore, useNavigation } from 'mycheck-core';
import { IFormikValues } from 'plugins/CheckoutPlugin/types/IFormikValues';
import { RefObject, useCallback } from 'react';
import { astralWalletPostMessageToIframe } from '../utils/astralWalletPostMessageToIframe';
import { useIframeMessageListener } from 'core/hooks/useIframeMessageListener';
import {
  AstralWalletIFrameMessage,
  AstralWalletParentMessage,
} from '../constants';

interface UseAstralWalletIframeMessageListenerProps {
  handleCloseIframe: () => void;
  formikRef: RefObject<FormikProps<IFormikValues>>;
}

export const useAstralWalletIframeMessageListener = ({
  handleCloseIframe,
  formikRef,
}: UseAstralWalletIframeMessageListenerProps) => {
  const CheckoutStore = useCheckoutStore();
  const AuthStore = useAuthStore();
  const navigation = useNavigation();

  const redirectToConfirmPage = useCallback(
    async (payload: unknown) => {
      const orderId = payload as string;
      await CheckoutStore.getOrderConfirmationDetails({
        model: { orderId },
        accessToken: AuthStore.accessToken,
      });
      navigation.push('/menu/confirm');
    },
    [AuthStore.accessToken, CheckoutStore, navigation],
  );

  const changePaymentMethod = useCallback(() => {
    handleCloseIframe();
  }, [handleCloseIframe]);

  const tryAgainPayment = useCallback(() => {
    handleCloseIframe();
    formikRef.current.submitForm();
  }, [formikRef, handleCloseIframe]);

  const cancelOrder = useCallback(
    async (payload: unknown) => {
      const cancelOrderDescription = payload as string;
      astralWalletPostMessageToIframe({
        type: AstralWalletParentMessage.TOGGLE_ASTRAL_WALLET_LOADER,
      });
      await CheckoutStore.refundAstralWalletPayment({
        model: {
          uuid: CheckoutStore.astralWalletTransactionData.paymentId,
        },
        accessToken: AuthStore.accessToken,
      });
      astralWalletPostMessageToIframe({
        type: AstralWalletParentMessage.TOGGLE_ASTRAL_WALLET_LOADER,
      });
      CheckoutStore.setCancelledOrderPaymentMessage(cancelOrderDescription);
      handleCloseIframe();
    },
    [handleCloseIframe],
  );

  const lockAstralWallet = useCallback(() => {
    CheckoutStore.setIsAstralWalletLocked(true);
  }, []);

  useIframeMessageListener<AstralWalletIFrameMessage>({
    [AstralWalletIFrameMessage.CHANGE_PAYMENT_METHOD]: changePaymentMethod,
    [AstralWalletIFrameMessage.CANCEL_ORDER]: cancelOrder,
    [AstralWalletIFrameMessage.REDIRECT_TO_CONFIRM_PAGE]: redirectToConfirmPage,
    [AstralWalletIFrameMessage.TRY_AGAIN_PAYMENT]: tryAgainPayment,
    [AstralWalletIFrameMessage.LOCK_ASTRAL_WALLET]: lockAstralWallet,
  });
};
